import { useFetcher } from "react-router";
import { useState, useEffect } from "react";
import type { GetGamesQuery } from "~/@graphql/types";

const TournamentsFilters: React.FC<{
  selectedGame: string;
  setSelectedGame: React.Dispatch<React.SetStateAction<string>>;
  selectedTeamSize: string;
  setSelectedTeamSize: React.Dispatch<React.SetStateAction<string>>;
  selectedStatus: string;
  setSelectedStatus: React.Dispatch<React.SetStateAction<string>>;
  groupFilters?: boolean;
}> = ({
  selectedGame,
  setSelectedGame,
  selectedTeamSize,
  setSelectedTeamSize,
  selectedStatus,
  setSelectedStatus,
  groupFilters,
}) => {
    const [showFilters, toggleFilters] = useState<boolean>(false);
    const fetcher = useFetcher<{ data: GetGamesQuery }>();

    useEffect(() => {
      fetcher.load("/games/get");
    }, []);

    const games = fetcher.data && fetcher.data.data ? fetcher.data.data.games : [];

    return (
      <nav className="mt-8 font-bold uppercase text-xs flex flex-wrap lg:flex-nowrap overflow-auto gap-x-6 gap-y-2">
        {!groupFilters && (
          <div className="flex flex-col gap-y-4">
            {/* Games */}
            <div className="flex flex-col gap-y-4 lg:gap-y-1 lg:w-full">
              <h4 className="hidden lg:block text-grey-dark">Games</h4>
              <div className="flex gap-x-4 lg:gap-x-2 text-xs font-bold">
                <button
                  type="button"
                  className={`uppercase rounded-xl px-2 shrink-0 border-2 ${selectedGame === "all"
                    ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                    : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                    }`}
                  onClick={() => setSelectedGame("all")}
                >
                  All
                </button>
                {games.map((game) => (
                  <button
                    key={`btn-${game.id}`}
                    type="button"
                    className={`uppercase rounded-xl px-1 shrink-0 border-2 ${game.id === selectedGame
                      ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                      : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                      }`}
                    onClick={() => setSelectedGame(game.id)}
                  >
                    {game.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-x-4">
              {/* Team Size */}
              <div className="flex flex-col gap-y-4 lg:gap-y-1">
                <h4 className="hidden lg:block text-grey-dark">Team size</h4>
                <div className="flex gap-x-4 lg:gap-x-2 text-xs font-bold">
                  <button
                    type="button"
                    className={`uppercase rounded-xl px-2 shrink-0 border-2 ${selectedTeamSize === "any"
                      ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                      : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                      }`}
                    onClick={() => setSelectedTeamSize("any")}
                  >
                    Any
                  </button>
                  {[
                    { id: "e0569343-bd06-11ed-840a-000d3a9bae12", name: "solo" },
                    { id: "e0569341-bd06-11ed-840a-000d3a9bae12", name: "duos" },
                    { id: "e0569342-bd06-11ed-840a-000d3a9bae12", name: "trios" },
                    { id: "e0569340-bd06-11ed-840a-000d3a9bae12", name: "4v4" },
                    { id: "e056933f-bd06-11ed-840a-000d3a9bae12", name: "5v5" },
                  ].map((teamSize) => (
                    <button
                      key={`btn-${teamSize.id}`}
                      type="button"
                      className={`uppercase rounded-xl px-1 shrink-0 border-2 ${teamSize.name === selectedTeamSize
                        ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                        : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                        }`}
                      onClick={() => setSelectedTeamSize(teamSize.name)}
                    >
                      {teamSize.name}
                    </button>
                  ))}
                </div>
              </div>
              {/* Tournament Status */}
              <div className="flex flex-col gap-y-4 lg:gap-y-1">
                <h4 className="hidden lg:block text-grey-dark">
                  Tournament Status
                </h4>
                <div className="flex gap-x-4 lg:gap-x-2 text-xs font-bold">
                  <button
                    type="button"
                    className={`uppercase rounded-xl px-2 shrink-0 border-2 ${selectedStatus === "any"
                      ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                      : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                      }`}
                    onClick={() => setSelectedStatus("any")}
                  >
                    Any
                  </button>
                  {["ongoing", "complete", "registering"].map((status) => (
                    <button
                      key={`btn-${status}`}
                      type="button"
                      className={`uppercase rounded-xl px-1 shrink-0 border-2 ${status === selectedStatus
                        ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                        : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                        }`}
                      onClick={() => setSelectedStatus(status)}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {groupFilters && (
          <div className="relative">
            <button
              type="button"
              className={`flex gap-2 items-center uppercase rounded-2xl px-3 py-1 shrink-0 border-2 border-grey-darker dark:border-grey-dark text-grey-darker dark:text-grey-dark text-sm ml-auto ${showFilters
                ? "border-[transparent] bg-jiwe-dark-blue !text-[#fff]"
                : ""
                }`}
              onClick={() => toggleFilters(!showFilters)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="12"
                viewBox="0 0 11 12"
              >
                <path
                  id="adjust"
                  d="M5.5,10.25V3.75m0,6.5a1,1,0,0,1,0,2m0-2a1,1,0,0,0,0,2m0,2.5v-2.5m8-2V3.75m0,6.5a1,1,0,1,1,0,2m0-2a1,1,0,0,0,0,2m0,2.5v-2.5m-4-6V3.75m0,2.5a1,1,0,1,1,0,2m0-2a1,1,0,0,0,0,2m0,6.5V8.25"
                  transform="translate(-4 -3.25)"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  className={`stroke-grey-darker dark:stroke-grey-dark`}
                  style={{
                    stroke: `${showFilters ? "#fff" : ""}`,
                  }}
                />
              </svg>
              <span className="dark:text-grey-dark">Filters</span>
            </button>
            {showFilters && (
              <div
                className="absolute top-12 z-10 shadow-sm w-48 h-28 right-0 bg-[#fff] dark:bg-bg-dark rounded-xl"
                style={{
                  boxShadow: "1px 6px 3px 0px #0000001c",
                  width: "70%",
                  height: "300px",
                  zIndex: 100,
                  border: "1px solid #eee",
                }}
              >
                <div className="flex flex-col gap-x-4 items-start p-4 text-sm font-bold">
                  {/* <button
                  type="button"
                  className={`uppercase rounded-xl px-2 shrink-0 border-2 ${
                    selectedGame === "all"
                      ? "border-jiwe-blue dark:border-jiwe-alt-yellow text-jiwe-blue dark:text-jiwe-alt-yellow"
                      : "text-grey-border dark:text-grey-dark border-grey-border dark:border-grey-dark"
                  }`}
                >
                  All
                </button> */}
                  {[
                    { id: "id", name: "Call of duty: mobile" },
                    { id: "codmw", name: "Call of duty: MW II" },
                    { id: "fifa", name: "FIFA" },
                  ].map((game) => (
                    <button
                      key={`btn-${game.id}`}
                      type="button"
                      className={`uppercase ${game.id === selectedGame
                        ? "text-jiwe-blue dark:text-jiwe-alt-yellow"
                        : "text-grey-dark"
                        }`}
                    // onClick={}
                    >
                      {game.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </nav>
    );
  };

export default TournamentsFilters;
